import {
  Box,
  Checkbox,
  Collapse,
  IconButton,
  CircularProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { PlanningGridContext } from "../../PlanningGrid";
import { SelectionContext } from "../../PlanningGrid";
import { Arrow } from "../../Arrow/Arrow";
import { RenderDates } from "../../RenderDates/RenderDates";
import { MoveItemArrowIcon } from "../../../../../components/VectorIcons/MoveItemArrowIcon";
import { ActionsMenu } from "../../../../../components/ActionsMenu/ActionsMenu";
import DeleteIcons from "../../../../ProjectSheet/ListFiles/BlockList/DeleteIcons/DeleteIcons";
import { toggleManyReducer } from "../BlockSection.utils";
import {
  normalizeChapterAttachments,
  toggleOneReducer,
} from "./ChapterSection.utils";
import { createSelectableAddendumItem } from "../../AddendumSection/addendumSection.utils";
import { ChapterAttachmentRow } from "./ChapterAttachmentRow";
import { AddendumSection } from "../../AddendumSection/AddendumSection";
import { ReactComponent as AddendumIcon } from "../../../../../assets/icons/addendum.svg";

export const ChapterSection = ({
  bloco,
  capitulo,
  contentId,
  isLastBlock,
  isLastChapter,
  canMoveToPreviousBlock,
  canMoveToNextBlock,
  moveToPreviousBlock,
  moveToNextBlock,
  handleDeleteAttachment,
  handleDeleteAddendum,
  handleDeleteChapter,
  attachmentActions,
  handleEditFile,
  handleOpenReleaseTexts,
}) => {
  const { headerGroups, grid, isSelectionMode, openIds, toggleOpenId } =
    useContext(PlanningGridContext);
  const { selectedFileIds, selectedFiles, setSelectedFiles } =
    useContext(SelectionContext);
  const [isPreviousLoading, setIsPreviousLoading] = useState(false);
  const [isNextLoading, setIsNextLoading] = useState(false);
  const hasVersions = !!capitulo.attachments.length;
  const hasContent = hasVersions || !!capitulo.addenda?.length;
  const hasAddenda = !!capitulo.addenda?.length;
  const hasAddendumInChapter = !!capitulo.addenda?.length;
  const hasAttachments = (capitulo.attachments || []).length > 0;
  const canDeleteChapter = isLastBlock && isLastChapter && !hasAttachments;

  const attachments = useMemo(
    () => normalizeChapterAttachments(capitulo, bloco),
    [capitulo, bloco]
  );

  const allChapterItems = useMemo(() => {
    const addendums = (capitulo.addenda || []).map((addendum) =>
      createSelectableAddendumItem(addendum, true)
    );
    return [...attachments, ...addendums];
  }, [attachments, capitulo.addenda]);

  const toggleMany = useCallback(
    (list) => {
      setSelectedFiles((prev) => toggleManyReducer(prev, list));
    },
    [setSelectedFiles]
  );

  const toggleOne = useCallback(
    (attachment) => {
      setSelectedFiles((prev) =>
        toggleOneReducer(prev, {
          ...attachment,
          blockNumber: bloco.number,
          chapterNumber: capitulo.number,
        })
      );
    },
    [setSelectedFiles, bloco.number, capitulo.number]
  );

  const allSelected = useMemo(
    () =>
      allChapterItems.length > 0 &&
      allChapterItems.every((a) => selectedFileIds.has(a.id)),
    [allChapterItems, selectedFileIds]
  );

  const someSelected = useMemo(
    () =>
      allChapterItems.some((a) => selectedFileIds.has(a.id)) && !allSelected,
    [allChapterItems, selectedFileIds, allSelected]
  );

  const handleMoveToPrevious = useCallback(async () => {
    setIsPreviousLoading(true);
    try {
      await moveToPreviousBlock();
    } finally {
      setIsPreviousLoading(false);
    }
  }, [moveToPreviousBlock]);

  const handleMoveToNext = useCallback(async () => {
    setIsNextLoading(true);
    try {
      await moveToNextBlock();
    } finally {
      setIsNextLoading(false);
    }
  }, [moveToNextBlock]);

  const sortedAttachments = useMemo(
    () => [...capitulo.attachments].sort((a, b) => b.version - a.version),
    [capitulo.attachments]
  );

  const isOpen = isSelectionMode || openIds.includes(`chapter-${capitulo.id}`);

  return (
    <Box>
      <Box
        display="grid"
        gridTemplateColumns={grid}
        sx={{
          bgcolor: isOpen ? "#F6F6FE" : "#ffffff",
          borderBottom: isOpen ? "1px solid transparent" : "1px solid #e0e2ff",
          minHeight: "48px",
          pl: "16px",
          pr: "16px",
        }}
      >
        {isSelectionMode && (
          <Box display="flex" justifyContent="center" alignItems="center">
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected}
              disabled={!hasVersions}
              onChange={() => toggleMany(allChapterItems)}
              sx={{
                color: "#43444D",
                "&.Mui-checked": { color: "#43444D" },
                "&.MuiCheckbox-indeterminate": { color: "#43444D" },
                "&.Mui-disabled": { color: "#CCCCCC" },
              }}
            />
          </Box>
        )}
        <Box
          display="flex"
          alignItems="center"
          sx={{ cursor: hasContent ? "pointer" : "default" }}
          onClick={() =>
            !isSelectionMode &&
            hasContent &&
            toggleOpenId(`chapter-${capitulo.id}`)
          }
        >
          {!isSelectionMode && hasVersions && (
            <Arrow
              isOpen={isOpen}
              margin="0 14px 0 16px"
              transparent={!hasContent}
            />
          )}
          <Typography
            fontWeight="bold"
            sx={{
              fontFamily: "inherit",
              fontSize: "14px",
              color: hasVersions ? "#575863" : "#CCCCCC",
              userSelect: "none",
              pl: hasVersions || isSelectionMode ? "0" : "16px",
            }}
          >
            {`Capítulo ${capitulo.number}`}
          </Typography>
        </Box>

        {/* Ajustar contrato para RenderDates ler datas em planning */}
        <RenderDates
          columns={headerGroups}
          planning={bloco.planning}
          realized={capitulo.realized}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            pr: "8px",
          }}
        >
          <Tooltip
            title="Possui adendo associado"
            placement="left"
            slotProps={{
              tooltip: {
                sx: {
                  bgcolor: "#FFFFFF",
                  color: "#1F1F29",
                  border: "1px solid #E2E4FF",
                  boxShadow: "0px 4px 12px rgba(31, 31, 41, 0.12)",
                  fontFamily: '"Globotipo Text", sans-serif',
                  fontSize: "12px",
                  fontWeight: 500,
                  p: "2px",
                },
              },
              arrow: {
                sx: {
                  color: "#FFFFFF",
                  "&:before": {
                    border: "1px solid #E2E4FF",
                  },
                },
              },
            }}
            arrow
          >
            <Box
              component={AddendumIcon}
              aria-label="Adendo"
              sx={{
                mr: "9px",
                visibility:
                  !isOpen && hasAddendumInChapter ? "visible" : "hidden",
              }}
            />
          </Tooltip>

          {!!canMoveToPreviousBlock &&
            !isSelectionMode &&
            (isPreviousLoading ? (
              <CircularProgress
                size={28}
                thickness={4}
                sx={{ color: "#0098cd" }}
              />
            ) : (
              <Tooltip title="Mover para o bloco anterior">
                <IconButton
                  size="small"
                  sx={{
                    border: "1px solid #E2E4FF",
                    transform: "rotate(180deg)",
                  }}
                  onClick={handleMoveToPrevious}
                >
                  <MoveItemArrowIcon />
                </IconButton>
              </Tooltip>
            ))}

          {!!canMoveToNextBlock &&
            !isSelectionMode &&
            (isNextLoading ? (
              <CircularProgress
                size={28}
                thickness={4}
                sx={{ color: "#0098cd" }}
              />
            ) : (
              <Tooltip title="Mover para o próximo bloco">
                <IconButton
                  size="small"
                  sx={{ border: "1px solid #E2E4FF" }}
                  onClick={handleMoveToNext}
                >
                  <MoveItemArrowIcon />
                </IconButton>
              </Tooltip>
            ))}

          {canDeleteChapter && (
            <ActionsMenu
              menuOptions={[
                {
                  action: () => handleDeleteChapter(capitulo),
                  label: "Remover",
                  icon: <DeleteIcons returnColor={() => "#000"} />,
                },
              ]}
              disabled={isSelectionMode}
            />
          )}
        </Box>
      </Box>

      {hasVersions && (
        <Collapse in={isOpen} timeout={0} unmountOnExit>
          <Box sx={{ bgcolor: "#F6F6FE", pl: "16px", pr: "16px", pb: "8px" }}>
            {sortedAttachments.map((attachment) => (
              <ChapterAttachmentRow
                key={`${attachment.chapterId}-${attachment.id}`}
                attachment={attachment}
                bloco={bloco}
                capitulo={capitulo}
                contentId={contentId}
                isChecked={selectedFileIds.has(attachment.id)}
                toggleOne={toggleOne}
                attachmentActions={attachmentActions}
                handleOpenReleaseTexts={handleOpenReleaseTexts}
                handleEditFile={handleEditFile}
                handleDeleteAttachment={handleDeleteAttachment}
              />
            ))}
          </Box>

          {hasAddenda && (
            <AddendumSection
              addenda={capitulo.addenda}
              label={`Adendos Capítulo ${capitulo.number}`}
              id={`addendum-chapter-${capitulo.id}`}
              scope="chapter"
              attachmentActions={attachmentActions}
              handleEditAddendum={(addendum) =>
                handleEditFile({
                  ...addendum.attachment,
                  addendumId: addendum.attachmentId,
                  isChapter: true,
                  isAddendum: true,
                  blockId: capitulo.blockId,
                  chapterId: capitulo.id,
                })
              }
              handleDeleteAddendum={handleDeleteAddendum}
              handleOpenReleaseTexts={handleOpenReleaseTexts}
              selectedFiles={selectedFiles}
              setSelectedFiles={setSelectedFiles}
            />
          )}
        </Collapse>
      )}
    </Box>
  );
};
